import { useEffect, useState } from "react";

/**
 * A React hook that provides a countdown to a target date, returning both the formatted string
 * and the raw time left in milliseconds. The countdown is formatted as `hh:mm:ss` if the remaining
 * time is an hour or more, and as `mm:ss` if it's less than an hour.
 *
 * @param {Date} targetDate - The local date and time to count down to.
 * @returns {{ format: string, timeLeft: number }} An object containing the formatted countdown
 * string and the raw time left in milliseconds.
 *
 * @example
 * const { format, timeLeft } = useCountDown(new Date("2024-12-31T23:59:59"));
 * console.log(format); // e.g., "01:30:45" or "59:30"
 * console.log(timeLeft); // e.g., 5432000 (milliseconds remaining)
 */
export const useCountDown = (targetDate: Date) => {
  // State to hold the time left in milliseconds
  const [timeLeft, setTimeLeft] = useState<number>(targetDate.getTime() - new Date().getTime());

  useEffect(() => {
    // Update the countdown every second
    const timer = setInterval(() => {
      const difference = targetDate.getTime() - new Date().getTime();
      if (difference <= 0) {
        // If the target date has passed, stop the countdown and set time left to 0
        setTimeLeft(0);
        clearInterval(timer);
      } else {
        setTimeLeft(difference);
      }
    }, 1000);

    // Clean up the interval when the component is unmounted or when targetDate changes
    return () => clearInterval(timer);
  }, [targetDate]);

  /**
   * Formats the time left into a string.
   *
   * @param {number} milliseconds - The time left in milliseconds.
   * @returns {string} The formatted time string in `hh:mm:ss` or `mm:ss`.
   */
  const formatTime = (milliseconds: number): string => {
    const hours = Math.floor((milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    if (milliseconds >= 3600000) {
      // More than an hour remaining
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    // Less than an hour remaining
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const reset = (newDate: Date) => {
    setTimeLeft(newDate.getTime() - new Date().getTime());
  };

  return { format: formatTime(timeLeft), timeLeft, reset };
};
